import { useEffect } from "react";

import isNil from "lodash/isNil";

import {
  MessagesAnalyticsKey,
  MessagesAnalyticsScreen,
} from "@/constants/analytics/Analytics";
import { ModalPath, TabPath } from "@/constants/Navigation";
import { PatientTreatmentPhases } from "@/constants/User";
import { router } from "@/crossPlatform/expo-router";
import { useGroupChannel, useSendbirdChat } from "@/crossPlatform/Sendbird";
import useEncodedNavigation from "@/hooks/useEncodedNavigation";
import useEncodedSearchParams from "@/hooks/useEncodedSearchParams";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import GroupChannelFragment from "@/ui/components/messages/fragments/GroupChannelFragment";
import { FullScreenSpinner } from "@/ui/core";
import { Box } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { toAbsolutePath } from "@/utils/navigation.util";
import { isWeb } from "@/utils/platform.util";

const Home = () => {
  const { channelDisplayName, channelUrl, userTreatmentPhase } =
    useEncodedSearchParams<{
      channelDisplayName: string;
      channelUrl: string;
      userTreatmentPhase: string;
    }>();

  const { navigateWithEncoding, replaceWithEncoding } = useEncodedNavigation();
  const { sdk } = useSendbirdChat();
  const { captureMessage } = useAppMonitor();
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    if (!isWeb() || !isNil(channelUrl)) return;
    captureMessage("channelUrl is missing from group-channel");
    router.replace(toAbsolutePath([TabPath.Messages]));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only: redirect on cold-load when params were never available (otherwise fires on tab-switch)
  }, []);

  useEffect(() => {
    if (isNil(channelDisplayName)) {
      captureMessage(`channelDisplayName is missing from ${channelUrl}`);
      return;
    }

    trackPageView(MessagesAnalyticsScreen.Channel, {
      [MessagesAnalyticsKey.ChannelName]: channelDisplayName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- due to captureMessage
  }, [channelDisplayName, trackPageView]);

  if (!isWeb() && isNil(channelUrl)) {
    captureMessage("channelUrl is missing from group-channel");
    throw new Error("channelUrl is required");
  }

  const { channel } = useGroupChannel(sdk, channelUrl);

  const isUserInPostTreatment =
    userTreatmentPhase === PatientTreatmentPhases.PostTreatment;

  if (isNil(channel)) {
    return <FullScreenSpinner />;
  }
  const handleChannelDetailsPressed = () => {
    navigateWithEncoding({
      params: {
        channelDisplayName,
        channelUrl,
      },
      pathname: toAbsolutePath([TabPath.Messages, ModalPath.ChannelDetails]),
    });
  };

  const handleDismiss = () => {
    if (!isWeb()) {
      router.back();
      return;
    }
    // on web, /messages may not be in the nav stack if the user landed here via refresh or direct URL
    replaceWithEncoding({
      params: {},
      pathname: toAbsolutePath([TabPath.Messages]),
    });
  };

  // on web, channel isn't 100% full screen width, so set bg-color to avoid default gray
  const [backgroundColor, flex] = isWeb()
    ? [ColorToken.NeutralBackgroundPrimary, 1]
    : [undefined, 1];

  return (
    <Box backgroundColor={backgroundColor} flex={flex}>
      <GroupChannelFragment
        channel={channel}
        channelDisplayName={channelDisplayName || channel.name}
        isUserInPostTreatment={isUserInPostTreatment}
        onDetailsPress={handleChannelDetailsPressed}
        onDismiss={handleDismiss}
      />
    </Box>
  );
};

export default Home;
